import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { impersonationApi, ImpersonateableUser } from '@/services/api';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Button } from '@/components/ui/Button';
import { logger } from '@/utils/logger';

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Preselect this user when the modal opens (e.g. launched from a
      specific user row/detail page). The search still works, so the
      admin can switch targets before confirming. */
  initialUser?: ImpersonateableUser;
}

export const ImpersonationModal: React.FC<ImpersonationModalProps> = ({ isOpen, onClose, initialUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<ImpersonateableUser | null>(null);
  const [reason, setReason] = useState('');
  const { startImpersonation, isLoading: isImpersonating } = useImpersonation();

  // Seed the preselected target on open; without an initialUser the
  // previous selection is kept (existing behavior for the header entry).
  useEffect(() => {
    if (isOpen && initialUser) setSelectedUser(initialUser)
  }, [isOpen, initialUser])

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['impersonateable-users', searchTerm],
    queryFn: async () => {
      try {
        const response = await impersonationApi.getImpersonateableUsers({
          search: searchTerm,
          limit: 50,
        });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch impersonateable users', error as Error);
        throw error;
      }
    },
    enabled: isOpen,
  });

  const handleImpersonate = async () => {
    if (!selectedUser) return;

    try {
      await startImpersonation(selectedUser.id, reason);
      onClose();
    } catch (error) {
      // Error is handled by useImpersonation
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-xl font-semibold">
              Impersonate User
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute start-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="w-full ps-10 pe-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* User List */}
            <div className="border border-neutral-200 rounded-lg max-h-64 overflow-y-auto mb-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (data?.users?.length ?? 0) > 0 ? (
                <div className="divide-y divide-neutral-200">
                  {data?.users?.map((user: ImpersonateableUser) => (
                    <button
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-neutral-50 transition-colors ${
                        selectedUser?.id === user.id ? 'bg-primary-50 border-s-4 border-primary-600' : ''
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-neutral-200 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-neutral-500" />
                        </div>
                      </div>
                      <div className="flex-1 text-start">
                        <p className="font-medium text-neutral-900">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-neutral-500">{user.email}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800">
                          {user.userType}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-neutral-500">
                  {searchTerm ? 'No users found matching your search' : 'No users available'}
                </div>
              )}
            </div>

            {/* Reason (optional) */}
            {selectedUser && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Reason for impersonation (optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={2}
                  placeholder="e.g., Investigating user issue, testing user experience..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}

            {/* Selected User Summary */}
            {selectedUser && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> You will be impersonating{' '}
                  <strong>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </strong>{' '}
                  ({selectedUser.email}). All actions will be performed as this user.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-neutral-50 rounded-b-lg">
            <Button onClick={onClose} variant="outline" disabled={isImpersonating}>
              Cancel
            </Button>
            <Button
              onClick={handleImpersonate}
              disabled={!selectedUser || isImpersonating}
            >
              {isImpersonating ? 'Starting...' : 'Start Impersonation'}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
